/**
 * Поп-ап «что нового» в основном приложении (#946).
 *
 * Состояние приезжает с `/users/me`, который контекст организаций и так
 * запрашивает при входе: отдельный запрос ради двух полей отодвинул бы первый
 * экран. Отметка пишется в тот же профиль пользователя.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { lockTier } from '@/lib/pro-trial';
import { WhatsNewGate } from './WhatsNewGate';

export function WebWhatsNew() {
  const { lastSeenRelease, userCreatedAt, currentOrganization } = useOrganization();
  const { canSeeBilling } = useRole();
  const { proTrial, answered: flagsAnswered } = useFeatureFlagsSafe();
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();

  const handleSeen = useCallback(
    async (version: string) => {
      await apiClient.users.markReleaseSeen(version);
      // Ростер организаций и состояние поп-апа приходят одним ответом; после
      // записи он устарел, и следующий его читатель должен увидеть отметку.
      await queryClient.invalidateQueries({ queryKey: ['user-data'] });
    },
    [apiClient, queryClient]
  );

  // The trial is read with the flags: the pop-up is shown once, and opened on
  // `/users/me` alone it would mark a feature the trial opened «Pro and up».
  if (!flagsAnswered) return null;

  return (
    <WhatsNewGate
      lastSeenRelease={lastSeenRelease}
      userCreatedAt={userCreatedAt}
      // Тариф — из ростера `/users/me`, а не из `TierContext`: тот для ролей без
      // `billing.read` подписку не запрашивает и отдаёт `free`, то есть поп-ап
      // либо врал бы Pro-организации, либо молчал бы Free-организации (#978).
      // `null` — организация не выбрана или воркер ещё без этого поля.
      // На пробе Pro — тариф замка (#2816), как у встроенного поп-апа
      // (`/embedded/whats-new` считает его на сервере): иначе Free на пробе
      // видел бы открытые ему функции помеченными «на Pro и выше».
      tier={lockTier(currentOrganization?.subscriptionTier ?? null, proTrial)}
      canViewPlans={canSeeBilling}
      onSeen={handleSeen}
    />
  );
}
